import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  LineSeries,
  Category,
  Tooltip,
  Inject,
  Legend,
} from "@syncfusion/ej2-react-charts";
import dayjs from "dayjs";
import { formatNumber } from "../../utils/formatNumber";

import { ChartControls } from "../../components/Channel";
import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";

import "../../components/charts3/Chart.css";

/* =========================
   HELPERS
========================= */
function minuteToLabel(i) {
  const h = String(Math.floor(i / 60)).padStart(2, "0");
  const m = String(i % 60).padStart(2, "0");
  return `${h}:${m}`;
}
/* =========================
   CSV PARSER
========================= */
function parseCSV(content) {
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  const data = [];
  
  // Skip header and parse numbers (German format: comma as decimal separator)
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line) {
      try {
        // Remove quotes and replace comma with dot for parsing
        const numberString = line.trim().replace(/"/g, "").replace(",", ".");
        const number = parseFloat(numberString);
        if (!isNaN(number)) {
          data.push(number);
        }
      } catch (err) {
        console.warn(`Unable to parse line: ${line}`);
      }
    }
  }
  
  return data;
}

/* =========================
   MAIN COMPONENT
========================= */
export default function ChannelCrossComparison({ channelId, refreshKey, refreshEnabled, setRefreshEnabled, refreshIntervalMin, setRefreshIntervalMin, refreshCountdown, manualRefresh, rawVideo }) {
  const [aggregation, setAggregation] = useState("1"); // "1" or "15"
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");
  const [externalData, setExternalData] = useState(null);
  const fileInputRef = useRef(null);

  // Chart ref for refresh functionality
  const chartRef = useRef(null);

  // Refresh chart when refreshKey changes
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.refresh();
  }, [refreshKey]);

  // Independent date state for this chart
  const [endDateInput, setEndDateInput] = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  });

  // Calculate start date based on mode (minute-only mode)
  const startDateInput = useMemo(() => {
    if (!endDateInput) return endDateInput;
    return endDateInput; // minute mode: start = end
  }, [endDateInput]);
  const handleYAxisLabelRender = useCallback((args) => {
    if (args.axis?.orientation === "Vertical") {
      const value = Number(args.value);
      if (!isNaN(value)) {
        args.text = formatNumber(value);
      }
    }
  }, []);
  const handleTooltipRender = useCallback((args) => {
    if (!args.point) return;

    args.text = `${args.point.x} : ${formatNumber(args.point.y)}`;
  }, []);

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      const parsed = parseCSV(content);
      setExternalData(parsed);
    };
    reader.readAsText(file);
  };

  // Get internal data for selected date
  const internalData = useMemo(() => {
    if (!rawVideo || !startDateInput) return [];

    const dateKey = dayjs(startDateInput, "YYYY-MM-DD").format("DD-MM-YYYY");
    const arr = rawVideo[dateKey] || [];

    const startMinute = parseInt(startTime.split(":")[0]) * 60 + parseInt(startTime.split(":")[1]);
    const endMinute = parseInt(endTime.split(":")[0]) * 60 + parseInt(endTime.split(":")[1]);

    return arr.slice(startMinute, endMinute + 1).map((val, i) => ({
      minute: startMinute + i,
      value: Math.round((Number(val) || 0) / 2),
    }));
  }, [rawVideo, startDateInput, startTime, endTime]);

  // Combine internal and external data
  const chartData = useMemo(() => {
    if (!internalData.length) return { internal: [], external: [] };

    const startMinute = parseInt(startTime.split(":")[0]) * 60 + parseInt(startTime.split(":")[1]);
    const interval = parseInt(aggregation);
    
    if (interval === 1) {
      // Minute by minute
      const internal = internalData.map((d) => ({
        x: minuteToLabel(d.minute),
        y: d.value,
      }));

      const external = externalData
        ? internalData.map((d, i) => ({
            x: minuteToLabel(d.minute),
            y: externalData[startMinute + i] || 0,
          }))
        : [];

      return { internal, external };
    } else {
      // Every 15 minutes
      const internal = [];
      const external = [];

      for (let i = 0; i < internalData.length; i += interval) {
        const chunk = internalData.slice(i, i + interval);
        const avg = chunk.reduce((sum, d) => sum + d.value, 0) / chunk.length;
        const minute = chunk[0].minute;

        internal.push({ x: minuteToLabel(minute), y: Math.round(avg) });

        if (externalData) {
          const extChunk = [];
          for (let j = 0; j < interval && startMinute + i + j < externalData.length; j++) {
            extChunk.push(externalData[startMinute + i + j] || 0);
          }
          const extAvg = extChunk.reduce((sum, val) => sum + val, 0) / extChunk.length;
          external.push({ x: minuteToLabel(minute), y: Math.round(extAvg) });
        }
      }

      return { internal, external };
    }
  }, [internalData, externalData, startTime, aggregation]);

  // Custom axis label rendering
  const handleAxisLabelRender = useCallback((args) => {
    if (!args || !args.text) return;
    // Keep the label as is (already in HH:mm format from x values)
  }, []);

  // --- LOGGER INTEGRATION ---
  const { logPageChange } = useUserJourneyLogger({
    channelId,
  });
  useEffect(() => {
    logPageChange({
      chartType: "crosscomparison",
    });
  }, [logPageChange]);

  return (
    <div style={{ padding: "20px 0" }}>
      {/* Chart Controls */}
      <div   style={{
          background: "var(--bg-card)",
          padding: "16px 20px",
          borderRadius: 12,
          marginBottom: 20,
          boxShadow: "0 2px 8px var(--shadow-color)",
          color: "var(--text-primary)",
        }}>
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          {/* Date and Time Controls */}
          <div style={{ flex: "1 1 auto", minWidth: 400 }}>
            <ChartControls
              channelId={channelId}
              mode="minute"
              onModeChange={() => {}}
              startDateInput={startDateInput}
              endDateInput={endDateInput}
              // Single-day mode: the picker only ever reports a start, and
              // this page defines the end as the same day.
              onStartDateChange={setEndDateInput}
              onEndDateChange={setEndDateInput}
              startTime={startTime}
              endTime={endTime}
              onStartTimeChange={setStartTime}
              onEndTimeChange={setEndTime}
              hideModeButtons={true}
              refreshEnabled={refreshEnabled}
              onRefreshToggle={setRefreshEnabled}
              refreshIntervalMin={refreshIntervalMin}
              onRefreshIntervalChange={setRefreshIntervalMin}
              countdown={refreshCountdown}
              onManualRefresh={manualRefresh}
            />
          </div>

          {/* Aggregation Buttons */}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => setAggregation("1")}
              style={{
                padding: "10px 20px",
                border: aggregation === "1"
                  ? "1px solid #3b82f6"
                  : "1px solid var(--border-color)",
                borderRadius: 6,
                background: aggregation === "1"
                  ? "rgba(59,130,246,0.15)"
                  : "var(--bg-secondary)",
                color: aggregation === "1"
                  ? "#3b82f6"
                  : "var(--text-primary)",
                fontWeight: aggregation === "1" ? 600 : 400,
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              1 Min
            </button>

           <button
              onClick={() => setAggregation("15")}
              style={{
                padding: "10px 20px",
                border: aggregation === "15"
                  ? "1px solid #3b82f6"
                  : "1px solid var(--border-color)",
                borderRadius: 6,
                background: aggregation === "15"
                  ? "rgba(59,130,246,0.15)"
                  : "var(--bg-secondary)",
                color: aggregation === "15"
                  ? "#3b82f6"
                  : "var(--text-primary)",
                fontWeight: aggregation === "15" ? 600 : 400,
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              15 Min
            </button>

          </div>

          {/* File Upload */}
          <div style={{ display: "flex", gap: 8 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                padding: "10px 24px",
                border: "none",
                borderRadius: 6,
                background: "#3b82f6",
                color: "#fff",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Press to Upload
            </button>
            {externalData && (
              <button
                onClick={() => {
                  setExternalData(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ef4444",
                  borderRadius: 6,
                  background: "transparent",
                  color: "#ef4444",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Clear
              </button>

            )}
          </div>
        </div>

        {externalData && (
          <div
            style={{
              marginTop: 12,
              padding: "6px 10px",
              background: "rgba(34,197,94,0.12)",
              border: "1px solid rgba(34,197,94,0.35)",
              borderRadius: 6,
              fontSize: 13,
              color: "var(--text-primary)",
            }}
          >
            ✓ Loaded: {externalData.length} values
          </div>

        )}
      </div>

    {/* Chart */}
    {internalData.length > 0 && (
      <div className="chart-container">
        <h3 className="chart-title">Cross Comparison</h3>

        <ChartComponent
          ref={chartRef}
          width="100%"
          height="450px"

          /* chart card rengi */
          background="var(--bg-card)"

          margin={{ right: 40, left: 15 }}

          primaryXAxis={{
            valueType: "Category",
            labelIntersectAction: "Rotate45",
            axisLabelRender: handleAxisLabelRender,

            labelStyle: {
              color: "var(--text-secondary)",
              size: "12px",
            },

            majorGridLines: {
              width: 1,
              color: "var(--chart-grid)",
            },

            majorTickLines: {
              width: 0,
            },

            lineStyle: {
              width: 0,
            },
          }}

          primaryYAxis={{
            title: "Avg Active TV",

            labelStyle: {
              color: "var(--text-secondary)",
              size: "12px",
            },

            titleStyle: {
              color: "var(--text-primary)",
              size: "13px",
              fontWeight: "500",
            },

            majorGridLines: {
              width: 1,
              color: "var(--chart-grid)",
            },

            majorTickLines: {
              width: 0,
            },

            lineStyle: {
              width: 0,
            },
          }}

          tooltip={{
            enable: true,
            shared: false,
            
          }}
          tooltipRender={handleTooltipRender}
          axisLabelRender={handleYAxisLabelRender}
          legendSettings={{
            visible: true,
            position: "Top",
            textStyle: {
              color: "var(--text-primary)",
              size: "12px",
            },
          }}
        >
          <Inject services={[LineSeries, Category, Tooltip, Legend]} />

          <SeriesCollectionDirective>
            <SeriesDirective
              dataSource={chartData.internal}
              xName="x"
              yName="y"
              name="Admongrel"
              type="Line"
              width={2}
              marker={{ visible: false }}
              fill="var(--chart-primary)"
            />

            {externalData && chartData.external.length > 0 && (
              <SeriesDirective
                dataSource={chartData.external}
                xName="x"
                yName="y"
                name="External Data"
                type="Line"
                width={2}
                marker={{ visible: false }}
                fill="var(--chart-secondary)"
              />
            )}
          </SeriesCollectionDirective>
        </ChartComponent>
      </div>
    )}

    </div>
  );
}
